import React, { useEffect, useState } from 'react';

import { TouchableOpacity, View } from 'react-native';

import { useTranslation } from 'react-i18next';

import { ArrowRightPlayerTitleSmall } from 'assets/images';

import { PLAYER_PROGRAM_TITLE } from 'config/testID';

import { VideoItem } from 'services/data-manager/types';

import { widthPercentageToDP } from 'utils/responsiveness';

import Text, { ETextCategory, ETextColor } from 'components/Text';

import usePlayerProgramTitle from '../hooks/usePlayerProgramTitle';
import styles from './styles';

type Props = {
    videoItem: VideoItem | undefined;
    isEmbeddedTabletPlayer: boolean;
    isEmbeddedWebPlayer: boolean;
    onOpenDetails?(): void;
    isContentLocked: boolean;
};

function TopEventInfo({
    isEmbeddedTabletPlayer,
    isEmbeddedWebPlayer,
    videoItem,
    onOpenDetails,
    isContentLocked,
}: Props) {
    const { t } = useTranslation();
    const isEmbeddedPlayer = isEmbeddedTabletPlayer || isEmbeddedWebPlayer;
    const numberOfLines = isEmbeddedPlayer ? 2 : 1;
    const title = usePlayerProgramTitle(videoItem);

    const [titleLines, setTitleLines] = useState<string[]>([]);

    useEffect(() => {
        if (videoItem) {
            if (isContentLocked) {
                setTitleLines([t('CARDS_TITLE_CONTENT_LOCKED')]);
            } else if (title) {
                const titleElement = document.getElementById(videoItem.id);

                if (titleElement) {
                    const lines = splitByLineBreaks(
                        title,
                        titleElement,
                        isEmbeddedPlayer
                            ? widthPercentageToDP(328)
                            : widthPercentageToDP(441, 560, 560),
                        numberOfLines,
                    );

                    setTitleLines(lines);
                }
            } else {
                setTitleLines([t('EPG_NO_INFO')]);
            }
        }
    }, [videoItem, title, numberOfLines, isEmbeddedPlayer, isContentLocked]);

    if (!videoItem) {
        return null;
    }

    return (
        <TouchableOpacity
            style={[
                styles.root,
                isEmbeddedTabletPlayer &&
                    styles.tabletEmbeddedProgramTitleBlock,
            ]}
            disabled={!title}
            onPress={onOpenDetails}
            testID={PLAYER_PROGRAM_TITLE}
            // @ts-ignore
            tabIndex={-1}
        >
            {titleLines.length <= 1 ? (
                <View style={styles.oneLineTitle}>
                    <Text
                        id={videoItem.id}
                        style={[
                            styles.title,
                            styles.multilineTitleTextWidth,
                            (isEmbeddedWebPlayer || isEmbeddedTabletPlayer) &&
                                styles.wideEmbeddedTitleText,
                        ]}
                        category={ETextCategory.title3}
                        color={ETextColor.neutral}
                    >
                        {titleLines.join(' ')}
                    </Text>
                    <View style={styles.titleArrowWrapper}>
                        <ArrowRightPlayerTitleSmall
                            height={16}
                            viewBox="0 0 8 10"
                        />
                    </View>
                </View>
            ) : (
                <>
                    <Text
                        id={videoItem.id}
                        style={[
                            styles.title,
                            styles.multilineTitleTextWidth,
                            (isEmbeddedWebPlayer || isEmbeddedTabletPlayer) &&
                                styles.wideEmbeddedTitleText,
                        ]}
                        category={ETextCategory.title3}
                        color={ETextColor.neutral}
                        testID={PLAYER_PROGRAM_TITLE}
                    >
                        {titleLines.join(' ')}

                        <View style={styles.titleArrowWrapper}>
                            <ArrowRightPlayerTitleSmall
                                height={16}
                                viewBox="0 0 8 10"
                            />
                        </View>
                    </Text>
                </>
            )}
        </TouchableOpacity>
    );
}

export default React.memo(TopEventInfo);

function splitByLineBreaks(
    text: string,
    element: HTMLElement,
    maxWidth: number,
    numberOfLines: number,
) {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    const lines = [''];

    if (context) {
        context.font = getComputedStyle(element).font;

        text.split(' ').forEach((word) => {
            const lastLine = lines[lines.length - 1];
            const updatedLine = lastLine ? `${lastLine} ${word}` : word;

            if (Math.floor(context.measureText(updatedLine).width) < maxWidth) {
                lines[lines.length - 1] = updatedLine;
            } else {
                lines.push(word);
            }
        });

        if (lines.length > numberOfLines) {
            const requiredLines = lines.slice(0, numberOfLines);

            return requiredLines.map((line, idx) =>
                idx === requiredLines.length - 1
                    ? `${line.substring(0, line.length - 5)}...`
                    : line,
            );
        }
    }

    return lines;
}
